import { Button, Divider, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BackButton from '../components/Buttons/BackButton';
import DetailsCard from '../components/DetailsCard/DetailsCard';
import Theme from '../Themes/main.theme';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';
import HttpService from '../services/HttpService';
import ActionButton from '../components/Buttons/ActionButton';
import CardStepContainer from '../components/CardStepsContainer/CardStepContainer';
import { CustomDataGrid } from '../components/AccordionControlled/AccordionControlled';
import {
  applications_columns,
  communication_columns,
  crmSearchCollectionsSubHeaderTitles,
  crmSearchForeisSubHeaderTitles,
  crmSearchHeaderTitles,
  crmSearchLocSubHeaderTitles,
  crmSearchMediaSubHeaderTitles,
  crmSearchProfSubHeaderTitles,
  entry_exit_card_columns,
  favourites_columns,
  newsletter_categories_columns,
  newsletter_columns,
  notes_columns,
  registrations_columns,
  zoom_columns,
} from '../lib/static/crm/columns';
import CollapsibleTable from '../components/CollapsibleTable/CollapsibleTable';
import TableGridWithFilters from '../components/TablegridWithFilters/TableGridWithFilters/TableGridWithFilters';
import DialogForm from '../components/Dialog/DialogForm';
import {
  createCommInitialValues,
  createNoteInitialValues,
  getNotesInitialValues,
} from '../lib/static/crm/initialValues';
import { Form, Formik, FormikContextType, FormikValues } from 'formik';
import {
  createCommunicationFields,
  createNoteFields,
  getNoteFields,
} from '../lib/static/crm/filterFields';
import {
  createCommValidationSchema,
  createNoteValidationSchema,
} from '../lib/static/crm/validationSchema';
import { useSnackbar } from 'notistack';
import AccordionCombination from '../components/AccordionControlled/AccordionCombination';
import { isSubmitEnabled, matchRefsWithData } from '../lib/helperFuncs';
import { aggregateRefs } from '../lib/static/crm/detailsRefs';
import FormLayout from '../components/layout/Form/FormLayout';
import { FormFields } from '../components/Dialog/helperFunctions';
import FormActionButtonLayout from '../components/layout/Form/FormActionButtonLayout';
import Details from '../components/Details/Details';
import { visitorProfileRefs } from '../lib/static/users/staticDetails';
import moment from 'moment';
import UserService from '../services/UserService';
import {
  PermissionsManageExternalUsers,
} from '../lib/permissions';

interface IVisitorDetails {
  id: number;
  full_name: string;
  email: string;
  phone_number: string;
  auth: string;
  afm: string;
  date_created: string;
  date_deleted: string;
  first_name: string;
  last_name: string;
  category: string;
  nomiki: boolean;
  is_enabled: boolean;
  can_be_deleted: boolean;
}

const ITEMS_PER_PAGE = 20;

const VisitorDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [totalPages, setTotalPages] = useState<number>(0);
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const handleOnPageChange = (page: number) => {
    setSelectedPage(page);
  };

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [toggleRefetch, setToggleRefetch] = useState<boolean>(true);
  const [toggleRefetchComms, setToggleRefetchComms] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [data, setData] = useState<IVisitorDetails>({
    id: 0,
    full_name: '',
    phone_number: '',
    auth: '',
    email: '',
    afm: '',
    date_created: '',
    date_deleted: '',
    first_name: '',
    last_name: '',
    category: '',
    nomiki: false,
    is_enabled: true,
    can_be_deleted: false,
  });

  const user_details = matchRefsWithData(
    data,
    visitorProfileRefs(data.is_enabled)
  );

  // CRM DATA
  const [registrations, setRegistrations] = useState([]);
  const [applications, setApplications] = useState([]);
  const [searchAudit, setSearchAudit] = useState([]);
  const [aggregateData, setAggregateData] = useState([]);
  const [newsletterData, setNewsletterData] = useState([]);
  const [newsletterCategoryData, setNewsletterCategoryData] = useState([]);
  const [zoomData, setZoomData] = useState([]); 

  document.title = t('Προβολή Καρτέλας Εξωτερικού Χρήστη');

  const getData = async () => {
    try {
      const response = await HttpService.getAxiosClient().get(
        `${process.env.REACT_APP_BACKEND_URL}/users/${id}`
      );
      setData({
        ...response.data,
        status: response.data.is_enabled
          ? t('Ενεργός')
          : t('Αποκλεισμένος'),
        category:
          response.data.category.length > 0
            ? response.data.category.map((x: any) => x.description).join(', ')
            : '-',
        roles: response.data.roles.map((role: any) => role.name),
        date_created: moment(response.data.date_created).format(
          'DD/MM/YYYY, hh:mm'
        ),
        date_deleted: response.data.date_deleted
          ? moment(response.data.date_deleted).format('DD/MM/YYYY, hh:mm')
          : '-',
        ...(!response.data.is_enabled && {
          disable_reason: response.data.disable_reason || '-',
        }),
      });
      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  // CRM CALLS
  const getRegistrations = async () => {
    try {
      const response = await HttpService.getAxiosClient().get(
        `${process.env.REACT_APP_BACKEND_URL}/applications/locations/applicant`,
        {
          params: {
            applicant_id: id,
            status: 4,
          },
        }
      );
      setRegistrations(response.data.data);
    } catch (error) {
      console.error(error);
    }
  };
  const getApplications = async () => {
    try {
      const response = await HttpService.getAxiosClient().get(
        `${process.env.REACT_APP_BACKEND_URL}/applications/locations/applicant`,
        {
          params: {
            applicant_id: id,
            status: '2,3,4,5,6',
          },
        }
      );
      setApplications(response.data.data);
    } catch (error) {
      console.error(error);
    }
  };

  const removeUnwantedLevelJSON = (a: any, keyName: string) => {
    Object.assign({}, a[keyName]);
    const flatObject = { ...a, ...removeUnwantedLevelJSON };
    delete flatObject[keyName];
    return flatObject;
  };

  /**
   * Return JSON values as following:
   * [{title: string, value: number}, {title: string, value: number}] -> returns [title, title]
   * [string, string] -> returns [string, string]
   * string -> returns string
   */
  const transformData = (response_data: any, fieldToTransform: string) => {
    return response_data.map((data: any) => {
      const obj = {};
      Object.keys(data[fieldToTransform]).map((aa) => {
        Object.assign(
          obj,
          Array.isArray(data[fieldToTransform][aa])
            ? typeof data[fieldToTransform][aa][0] === 'object'
              ? {
                [aa]: data[fieldToTransform][aa].map(
                  (title: any) => title.title
                ),
              }
              : {
                [aa]: data[fieldToTransform][aa].map((title: any) => title),
              }
            : {
              [aa]: data[fieldToTransform][aa],
            }
        );
      });
      return { ...data, ...obj };
    });
  };

  const getSearchAudit = async () => {
    try {
      const response = await HttpService.getAxiosClient().get(
        `${process.env.REACT_APP_BACKEND_URL}/crm/search/${id}`,
        {
          params: {
            page: selectedPage,
            per_page: ITEMS_PER_PAGE,
          },
        }
      );
      setTotalPages(response.data.total_count);
      let body = transformData(response.data.data, 'filters');
      body = body.map((data: any) => removeUnwantedLevelJSON(data, 'filters'));

      setSearchAudit(body);
    } catch (error) {
      console.error(error);
    }
  };

  const createNote = async (values: FormikContextType<FormikValues>) => {
    try {
      await HttpService.getAxiosClient().post(
        `${process.env.REACT_APP_BACKEND_URL}/crm/notes/${id}`,
        values
      );
      setToggleRefetch(!toggleRefetch);
      enqueueSnackbar(t('Η Σημείωση Προστέθηκε Επιτυχώς'), {
        variant: 'success',
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        t('Υπήρξε Πρόβλημα κατά την δημιουργία Σημείωσης! Προσπαθήστε Ξανά!'),
        { variant: 'success' }
      );
    } finally {
      setOpen(false);
    }
  };

  const getAggregateInfo = async () => {
    try {
      const response = await HttpService.getAxiosClient().get(
        `${process.env.REACT_APP_BACKEND_URL}/crm/aggregate/${id}`
      );
      
      setAggregateData(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getNewsletterInfo = async () => {
    try {
      const response = await HttpService.getAxiosClient().get(
        `${process.env.REACT_APP_BACKEND_URL}/crm/newsletter/${id}`
      );
      setNewsletterData(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getNewsletterCategoryInfo = async () => {
    try {
      const response = await HttpService.getAxiosClient().get(
        `${process.env.REACT_APP_BACKEND_URL}/crm/newsletter-category/${id}`
      );
      setNewsletterCategoryData(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getZoomCategoryInfo = async () => {
    try {
      const response = await HttpService.getAxiosClient().get(
        `${process.env.REACT_APP_BACKEND_URL}/zoom/meetings/details`, {
          params: {id}
        });        
      setZoomData(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const onReset = (formik: any) => {
    Object.keys(formik.values).map((value) => {
      formik.setFieldValue(value, '');
      formik.setFieldTouched(value, false);
    });
  };

  const submitCommunication = async (formik: any) => {
    try {
      await HttpService.getAxiosClient().post(
        `${process.env.REACT_APP_BACKEND_URL}/crm/communication/${id}`,
        {
          ...formik.values,
          date: new Date(
            formik.values.date.getFullYear(),
            formik.values.date.getMonth(),
            formik.values.date.getDate(),
            formik.values.time.$d.getHours(),
            formik.values.time.$d.getMinutes()
          ),
        }
      );
      enqueueSnackbar(t('Επιτυχής Καταγραφή Τηλεφωνικής Επικοινωνίας'), {
        variant: 'success',
      });
      setToggleRefetchComms(!toggleRefetchComms);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        t('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.'),
        { variant: 'error' }
      );
    } finally {
      onReset(formik);
    }
  };

  useEffect(() => {
    getSearchAudit();
  }, [selectedPage]);

  useEffect(() => {
    Promise.all([
      getData(),
      getRegistrations(),
      getApplications(),
      getAggregateInfo(),
      getNewsletterInfo(),
      getNewsletterCategoryInfo(),
      getZoomCategoryInfo()
    ]);

  }, []);

  const accordions = [
    {
      title: t('Τηλεφωνική Επικοινωνία'),
      children: (
        <>
          <Formik
            onSubmit={() => undefined}
            enableReinitialize
            initialValues={createCommInitialValues}
            validationSchema={createCommValidationSchema}
            validateOnBlur={false}
          >
            {(formik) => (
              <>
                <Form>
                  <FormLayout>
                    {FormFields(createCommunicationFields, formik)}
                  </FormLayout>
                  <FormActionButtonLayout>
                    <Button variant='outlined' onClick={() => onReset(formik)}>
                      {t('Καθαρισμός')}
                    </Button>
                    <Button
                      variant='contained'
                      disabled={isSubmitEnabled(formik?.errors, formik?.dirty)}
                      onClick={() => submitCommunication(formik)}
                    >
                      {t('Δημιουργία')}
                    </Button>
                  </FormActionButtonLayout>
                </Form>
              </>
            )}
          </Formik>
          <TableGridWithFilters
            hideFilters={true}
            pageSize={15}
            rowsPerPageOptions={[15, 30, 45]}
            sortingMode='server'
            paginationMode='server'
            columns={communication_columns}
            fetchUrl={`${process.env.REACT_APP_BACKEND_URL}/crm/communication/${id}`}
            reFetch={toggleRefetchComms}
          />
        </>
      ),
    },
    {
      title: t('Συγκεντρωτικά Στοιχεία'),
      children: (
        <CardStepContainer
          isLoading={isLoading}
          detailsProps={{
            rows: matchRefsWithData(aggregateData, aggregateRefs),
            removeLines: true,
            renderAsStripped: true,
          }}
        />
      ),
    },
    {
      title: t('Σημειώσεις'),
      children: (
        <TableGridWithFilters
          componentInBetween={
            <Box display='flex' justifyContent='end'
              mt={4}>
              <Button variant='contained' onClick={() => setOpen(true)}>
                {t('+ Προσθήκη Σημείωσης')}
              </Button>
              <DialogForm
                title={t('Δημιουργία Σημείωσης') as string}
                validationSchema={createNoteValidationSchema}
                fields={createNoteFields}
                initialValues={createNoteInitialValues}
                open={open}
                handleClose={() => setOpen(false)}
                onAccept={createNote}
              />
            </Box>
          }
          showTitle={false}
          showDivider={false}
          oneLiner={true}
          showCard={false}
          pageSize={15}
          rowsPerPageOptions={[15, 30, 45]}
          sortingMode='server'
          paginationMode='server'
          initialValues={getNotesInitialValues}
          filterFields={getNoteFields}
          columns={notes_columns}
          fetchUrl={`${process.env.REACT_APP_BACKEND_URL}/crm/notes/${id}`}
          reFetch={toggleRefetch}
        />
      ),
    },
    {
      title: t('Αναζητήσεις'),
      children:
        searchAudit.length > 0
          ? (
            <CollapsibleTable
              extraInfoHeader={t('Φίλτρα Αναζήτησης') as string}
              rows={searchAudit}
              headerTitles={crmSearchHeaderTitles}
              subHeaderTitles={[
                { key: t('Τοποθεσίες'), value: crmSearchLocSubHeaderTitles },
                { key: t('Επαγγελματίες'), value: crmSearchProfSubHeaderTitles },
                {
                  key: t('Αναζήτηση στις Συλλογές'),
                  value: crmSearchCollectionsSubHeaderTitles,
                },
                {
                  key: t('Αναζήτηση στους Φορείς'),
                  value: crmSearchMediaSubHeaderTitles,
                },
                {
                  key: t('Αναζήτηση Φορέων'),
                  value: crmSearchForeisSubHeaderTitles,
                },
              ]}
              subHeaderBasedOn='search_kind'
              pagination={{
                enablePagination: true,
                itemsPerPage: ITEMS_PER_PAGE,
                totalResults: totalPages,
                selectedPage: selectedPage,
                handleOnPageChange: handleOnPageChange,
              }}
            />
          )
          : (
            <Box justifyContent='center' display='flex'>
              <Typography variant='subtitle1' fontWeight='bold'>
                {t('Δεν υπάρχουν Αναζητήσεις')}
              </Typography>
            </Box>
          ),
    },
    {
      title: t('Αγαπημένα'),
      children: (
        <TableGridWithFilters
          hideFilters
          sortingMode='server'
          paginationMode='server'
          columns={favourites_columns}
          fetchUrl={`${process.env.REACT_APP_BACKEND_URL}/crm/favourites/${id}`}
        />
      ),
    },
    {
      title: t('Αιτήματα'),
      children: CustomDataGrid({
        columns: applications_columns,
        rowsPerPageOptions: [10, 20, 50],
        pageSize: 10,
        rows: applications,
        isLoading,
        addPagination: true,
      }),
    },
    {
      title: t('Καταχωρίσεις'),
      children: CustomDataGrid({
        columns: registrations_columns,
        rowsPerPageOptions: [10, 20, 50],
        pageSize: 10,
        rows: registrations,
        isLoading,
        addPagination: true,
      }),
    },
    {
      title: t('Δελτίο Εισόδου Εξόδου'),
      children: (
        <TableGridWithFilters
          hideFilters={true}
          pageSize={15}
          rowsPerPageOptions={[15, 30, 45]}
          sortingMode='server'
          paginationMode='server'
          columns={entry_exit_card_columns}
          fetchUrl={`${process.env.REACT_APP_BACKEND_URL}/crm/logs/${id}`}
        />
      ),
    },
    {
      title: 'Newsletter',
      children: CustomDataGrid({
        columns: newsletter_columns,
        rowsPerPageOptions: [10, 20, 50],
        pageSize: 10,
        rows: newsletterData,
        isLoading,
        addPagination: true,
      }),
    },
    {
      title: 'Newsletter ανά Κατηγορία',
      children: CustomDataGrid({
        columns: newsletter_categories_columns,
        rowsPerPageOptions: [10, 20, 50],
        pageSize: 10,
        rows: newsletterCategoryData,
        isLoading,
        addPagination: true,
      })
    },
    {
      title: 'Zoom Meetings / Webinars',
      children: CustomDataGrid({
        columns: zoom_columns,
        rowsPerPageOptions: [10, 20, 50],
        pageSize: 10,
        rows: zoomData,
        isLoading,
        addPagination: true,
      }),
    },
  ];

  return (
    <>
      <Box mt={2} mb={2}>
        <BackButton onClick={() => navigate('/admin/users/dashboard')} />
      </Box>
      <Typography variant='h2' color={Theme?.labelColor?.main}>
        {t('Προβολή Καρτέλας Εξωτερικού Χρήστη')}
      </Typography>
      <Divider sx={{ my: 3, border: 1, color: Theme?.dividerColor?.main }} />
      <Box maxWidth='700px'>
        <DetailsCard
          actionButtons={
            <>
              <ActionButton
                disabled={
                  !UserService.hasRole([
                    PermissionsManageExternalUsers.PermissionInternalManageExternalUserChangeRole,
                  ])
                }
                title={t('Επεξεργασία Χρήστη')}
                icon={<EditIcon />}
                onClick={() => navigate(`/admin/visitor/edit/${id}`)}
              />
              {/* {UserService.hasRole([
                PermissionsManageInternalUsers.PermissionInternalUsersDelete,
              ]) && (
                <ActionButton
                  title={t('Διαγραφή Χρήστη')} 
                  icon={
                    <Delete color={
                      data.can_be_deleted
                        ? 'error'
                        : 'disabled'
                    }/>
                  }
                  onClick={() => setOpen(true)}
                  disabled={!data?.can_be_deleted} 
                />
              )} */}
            </>
          }
          icon={<PersonIcon fontSize='large' />}
          title={data.full_name}
          isLoading={isLoading}
        >
          <Details rows={user_details} removeLines />
        </DetailsCard>
      </Box>
      {UserService.hasRole([
        PermissionsManageExternalUsers.PermissionInternalManageExternalUserNote,
      ]) && (
        <Box my={2}>
          <AccordionCombination accordions={accordions} />
        </Box>
      )}
    </>
  );
};

export default VisitorDetails;
